import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import type { ActiveSession, Subscriber, SubscriberIpAddressesItem } from '../../api/model';
import { DeleteSubscriberButton } from './DeleteSubscriberButton';

const displayValue = (value: string) => (value?.trim() ? value : '—');

type SubscriberDetailsCardProps = {
  subscriber: Subscriber;
  onEdit: () => void;
  onDeleteSuccess: () => void;
};

type SessionFieldProps = {
  label: string;
  value: string;
};

const SessionField = ({ label, value }: SessionFieldProps) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{displayValue(value)}</Typography>
  </Box>
);

const BngSessionDetails = ({ session }: { session: ActiveSession }) => (
  <Stack spacing={2}>
    <SessionField label="IPv4 Address" value={session.framed_ip_address} />
    <SessionField label="IPv6 Address" value={session.framed_ipv6_address} />
    <SessionField label="IPv6 PD" value={session.delegated_ipv6_prefix} />
    <SessionField label="Agent Circuit ID" value={session.agent_circuit_id} />
  </Stack>
);

export const SubscriberDetailsCard = ({
  subscriber,
  onEdit,
  onDeleteSuccess,
}: SubscriberDetailsCardProps) => {
  const hasActiveSession = subscriber.active_session !== null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" gutterBottom>
            Subscriber Details
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onEdit}>
              Edit
            </Button>
            <DeleteSubscriberButton username={subscriber.username} onSuccess={onDeleteSuccess} />
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Username
            </Typography>
            <Typography variant="body1">{subscriber.username}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Speed Profile
            </Typography>
            <Typography variant="body1">{subscriber.speed_profile}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Subscription Status
            </Typography>
            <Chip
              label={subscriber.is_suspended ? 'Service Suspended' : 'Service Active'}
              color={subscriber.is_suspended ? 'error' : 'success'}
              size="small"
            />
          </Box>

          {subscriber.is_suspended && subscriber.suspended_reason && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Suspended Reason
              </Typography>
              <Typography variant="body1">{subscriber.suspended_reason}</Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              BNG Session
            </Typography>
            <Chip
              label={hasActiveSession ? 'Online' : 'Offline'}
              color={hasActiveSession ? 'success' : 'default'}
              size="small"
            />
          </Box>

          {subscriber.is_suspended && hasActiveSession && (
            <Alert severity="info">Subscriber suspended in Pitstop but BNG session still active</Alert>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Static IP Addresses
            </Typography>
            {subscriber.ip_addresses.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {subscriber.ip_addresses.map((ip: SubscriberIpAddressesItem, index: number) => (
                  <Chip key={index} label={ip} variant="outlined" size="small" />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No static IP addresses assigned
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              BNG Session
            </Typography>
            {subscriber.active_session ? (
              <BngSessionDetails session={subscriber.active_session} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active BNG session
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
